import { useQuery, type UseQueryResult } from 'react-query';

import { getHeatDataV2 } from '../api/api';
import { CACHE_TIME, ENGAGEMENT_DATA_QUERY_KEY } from '../constants-types/HeatmapData';
import { HEATMAP_DATA_TYPES } from '../constants-types/HeatmapDataTypes';

type useGetEngagementDataReturnedValue = UseQueryResult<
  HeatmapAPI.GetHeatDataResponse<HeatmapDataTypeEngagement>,
  Error
> | null;

export interface useGetEngagementDataParams {
  siteId: SiteId;
  deviceType: HeatmapDeviceTypes;
  url: string;
  matchOperation: TargetingValue;
  jsonQueryString: string;
  screenshotId: number | null;
  enabled: boolean;
  onEngagementZonesDataError: () => void;
}

export const useGetEngagementData = ({
  siteId,
  deviceType,
  url,
  matchOperation,
  jsonQueryString,
  screenshotId,
  enabled,
  onEngagementZonesDataError,
}: useGetEngagementDataParams): useGetEngagementDataReturnedValue => {
  const type = HEATMAP_DATA_TYPES.ENGAGEMENT;
  const engagementData = useQuery<
    HeatmapAPI.GetHeatDataResponse<HEATMAP_DATA_TYPES.ENGAGEMENT>,
    Error
  >(
    [
      ENGAGEMENT_DATA_QUERY_KEY,
      { siteId, deviceType, url, matchOperation, type, jsonQueryString, screenshotId },
    ],
    () =>
      getHeatDataV2({
        siteId,
        type,
        screenshotId: screenshotId ?? 0,
        url,
        matchOperation,
        jsonQueryString,
      }),
    {
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: CACHE_TIME,
      cacheTime: CACHE_TIME,
      enabled: screenshotId !== null && enabled,
      onError: () => onEngagementZonesDataError(),
    },
  );

  if (!enabled || screenshotId === null) return null;

  return engagementData;
};
